// /////////////////////////////////////////////////////////////
// [ファイル] useIsoDocuments.ts
// [概要] ISO(QMS/ISMS 統合マネジメントシステム)文書プラットフォームのデータ層。
//        Cloud Run(/api 経由)の iso エンドポイントを Axios で叩き、規格・条項ツリー・
//        自社文書/条項・条項紐付けの参照を提供する。起動時に規格と文書一覧を1度だけ取得し、
//        条項ツリー/文書詳細/検索/紐付けは要求時に遅延取得する(useCrmTable と同系統)。
// /////////////////////////////////////////////////////////////
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ep } from '../api/endpoints';

export interface IsoStandard {
  id: number;
  standard_code: string;
  edition?: string | null;
  title?: string | null;
  certifier_body?: string | null;
  enabled?: boolean;
  clause_count?: number;
}

export interface IsoClause {
  id: number;
  standard_id: number;
  parent_id: number | null;
  clause_no: string;
  title?: string | null;
  full_title?: string | null;
  content?: string | null;
  display_order: number;
}

export interface IsoDocument {
  id: number;
  doc_code?: string | null;
  title: string;
  doc_type?: string | null;
  primary_standard_id?: number | null;
  primary_standard_code?: string | null;
  scope?: string | null;
  status?: string | null;
  current_version?: string | null;
  drive_file_id?: string | null;
  web_view_link?: string | null;
  mime_type?: string | null;
  source_clause_no?: string | null;
  mgmt_system?: string | null;
  owner_name?: string | null;
  source_group?: string | null;
  section_count?: number;
  revision_count?: number;
}

export interface IsoSection {
  id: number;
  document_id: number;
  parent_id: number | null;
  section_no?: string | null;
  title?: string | null;
  full_title?: string | null;
  content?: string | null;
  display_order: number;
  // 検索結果に付随する文書メタ
  doc_code?: string | null;
  document_title?: string | null;
}

export interface IsoMapping {
  mapping_id: number;
  note?: string | null;
  // clause 側(section 起点の照会で埋まる)
  id?: number;
  clause_no?: string | null;
  title?: string | null;
  standard_code?: string | null;
  // section 側(clause 起点の照会で埋まる)
  section_id?: number;
  section_no?: string | null;
  document_title?: string | null;
  doc_code?: string | null;
}

export interface IsoRevision {
  id: number;
  version: string;
  revised_at?: string | null;
  revised_by?: string | null;
  summary?: string | null;
}

export interface IsoDocumentDetail {
  document: IsoDocument;
  sections: IsoSection[];
  revisions: IsoRevision[];
  mappings: IsoMapping[];
}

// SoA 行の「対応自社規定」1件(所属文書つき、ドリルダウン用に本文も含む)
export interface IsoSoaMappedItem {
  section_id: number;
  section_no: string | null;
  title: string | null;
  content: string | null;
  document_id?: number | null;
  doc_code: string | null;
  document_title: string | null;
  drive_file_id?: string | null;
  gcs_path?: string | null;
}

// 適用宣言書(SoA)の管理策別行。附属書A管理策 + 当社の意思決定(applicable/status/justification) + 対応自社規定。
export interface IsoSoaRow {
  clause_id: number;
  clause_no: string;
  title: string | null;
  category_no: string | null;
  category_title: string | null;
  soa_id: number | null;
  applicable: boolean | null;
  status: string | null;
  justification: string | null;
  control_owner: string | null;
  remarks: string | null;
  soa_updated_at: string | null;
  mapped_count: number;
  mapped_items: IsoSoaMappedItem[];
}

export interface IsoSoaInput {
  clause_id: number;
  applicable?: boolean | null;
  status?: string | null;
  justification?: string | null;
  control_owner?: string | null;
  remarks?: string | null;
  updated_by?: string | null;
}

async function post(name: string, body: unknown): Promise<any> {
  const res = await axios.post(ep(name), body);
  return res.data;
}

export function useIsoDocuments() {
  const [standards, setStandards] = useState<IsoStandard[]>([]);
  const [documents, setDocuments] = useState<IsoDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [s, d] = await Promise.all([
          post('get_iso_standards', {}),
          post('get_iso_documents', { page: 0, pageSize: 200 }),
        ]);
        if (!alive) return;
        if (s?.status === 'success') setStandards(s.rowsResult || []);
        if (d?.status === 'success') setDocuments(d.rowsResult || []);
      } catch (e: any) {
        if (alive) setError(e?.message || 'ISOデータの読み込みに失敗しました');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const loadClauses = useCallback(async (standardId: number): Promise<IsoClause[]> => {
    const r = await post('get_iso_clauses', { standard_id: standardId });
    return r?.status === 'success' ? r.rowsResult || [] : [];
  }, []);

  const loadDocumentDetail = useCallback(async (docId: number): Promise<IsoDocumentDetail | null> => {
    const r = await post('get_iso_document_detail', { doc_id: docId });
    return r?.status === 'success' ? r.data : null;
  }, []);

  const searchSections = useCallback(
    async (params: { keyword?: string; standardCode?: string; clauseNo?: string; limit?: number }): Promise<IsoSection[]> => {
      const r = await post('search_iso_sections', params);
      return r?.status === 'success' ? r.rowsResult || [] : [];
    },
    []
  );

  const loadMappedClauses = useCallback(
    async (arg: { sectionId?: number; clauseId?: number }): Promise<IsoMapping[]> => {
      const r = await post('get_iso_mapped_clauses', {
        section_id: arg.sectionId,
        clause_id: arg.clauseId,
      });
      return r?.status === 'success' ? r.rowsResult || [] : [];
    },
    []
  );

  const loadSoa = useCallback(async (standardId: number): Promise<IsoSoaRow[]> => {
    const r = await post('get_iso_soa', { standard_id: standardId });
    return r?.status === 'success' ? r.rowsResult || [] : [];
  }, []);

  const upsertSoa = useCallback(async (input: IsoSoaInput): Promise<number | null> => {
    const r = await post('upsert_iso_soa', input);
    return r?.status === 'success' ? r.id : null;
  }, []);

  return {
    standards,
    documents,
    loading,
    error,
    loadClauses,
    loadDocumentDetail,
    searchSections,
    loadMappedClauses,
    loadSoa,
    upsertSoa,
  };
}
